import { defineMessages } from 'react-intl';

import { type ValueOf } from 'src/types';

export const ActionTypes = Object.freeze({
  SOURCE_CARD_OPENED: 'SOURCE_CARD_OPENED',
  SOURCE_CARD_OPTION_CLICKED: 'SOURCE_CARD_OPTION_CLICKED',
} as const);

export const SourceCardCSSConstants = Object.freeze({
  FADETIMEOUT: 150,
  LINEHEIGHT: 48,
  MAXSUBMENUHEIGHT: 268,
  MENUWIDTH: 282,
  OVERLAPSHIFT: '80%',
  PADDING: { x: 18, y: 20 },
  SUBMENUCLASSNAME: 'SourceCard__subMenu__item',
  SUBMENUSHIFT: '200%',
  TUNERBARHEIGHT: 71, // actual height + 5px of spacing
  UNIT: 'px',
} as const);

export const SourceCardConstants = Object.freeze({
  ADDTOCOLLECTION: 'addToCollection',
  ADDTOPLAYLIST: 'addToPlaylist',
  CLICK: 'click',
  CONTEXTMENU: 'contextmenu',
  DELETE: 'delete',
  DELETEPLAYLIST: 'deletePlaylist',
  DELETESTATION: 'deleteStation',
  EDITSTATION: 'editStation',
  NEWPLAYLIST: 'newPlaylist',
  OPENINDESKTOPAPP: 'openInDesktopApp',
  REMOVEFROMCOLLECTION: 'removeFromCollection',
  REMOVEFROMPLAYLIST: 'removeFromPlaylist',
  SCROLLTHRESHOLD: 10,
  SHARE: 'share',
  STARTSTATIONARTIST: 'startStationArtist',
  STARTSTATIONTRACK: 'startStationTrack',
  TIRED: 'tired',
  WHYSELECTED: 'whySelected',
} as const);

export const SourceCardSubmenuComponents = Object.freeze({
  addToPlaylist: {
    component: 'AddToPlaylistSubmenu',
    domain: 'collection',
  },
} as const);

interface ISourceCardPermissions {
  isRadioOnly: boolean;
  isUnavailable: boolean;
  hasNoRadio: boolean;
  isRetired: boolean;
}

export const SourceCardActionsDisabled = Object.freeze({
  [SourceCardConstants.ADDTOCOLLECTION]: {
    isUnavailable: true,
    isRadioOnly: true,
    isRetired: true,
  },
  [SourceCardConstants.REMOVEFROMCOLLECTION]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.NEWPLAYLIST]: {
    isUnavailable: true,
    isRadioOnly: true,
  },
  [SourceCardConstants.ADDTOPLAYLIST]: {
    isUnavailable: true,
    isRadioOnly: true,
  },
  [SourceCardConstants.REMOVEFROMPLAYLIST]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.STARTSTATIONTRACK]: {
    isUnavailable: true,
    isRadioOnly: false,
    hasNoRadio: true,
  },
  [SourceCardConstants.STARTSTATIONARTIST]: {
    isUnavailable: false,
    isRadioOnly: false,
    hasNoRadio: true,
  },
  [SourceCardConstants.SHARE]: {
    isUnavailable: true,
    isRadioOnly: false,
    isRetired: true,
  },
  [SourceCardConstants.DELETESTATION]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.DELETE]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.DELETEPLAYLIST]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.TIRED]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
  [SourceCardConstants.WHYSELECTED]: {
    isUnavailable: false,
    isRadioOnly: false,
  },
} as Record<ValueOf<typeof SourceCardConstants>, Partial<ISourceCardPermissions>>);

export const SourceCardItemsForNonPremium = {
  [SourceCardConstants.DELETEPLAYLIST]: false,
  [SourceCardConstants.ADDTOPLAYLIST]: false,
  [SourceCardConstants.REMOVEFROMPLAYLIST]: false,
  [SourceCardConstants.ADDTOCOLLECTION]: true,
  [SourceCardConstants.REMOVEFROMCOLLECTION]: true,
  [SourceCardConstants.OPENINDESKTOPAPP]: true,
  [SourceCardConstants.STARTSTATIONTRACK]: true,
  [SourceCardConstants.STARTSTATIONARTIST]: true,
  [SourceCardConstants.DELETESTATION]: true,
  [SourceCardConstants.SHARE]: true,
  [SourceCardConstants.DELETE]: true,
  [SourceCardConstants.EDITSTATION]: true,
  [SourceCardConstants.TIRED]: true,
  [SourceCardConstants.WHYSELECTED]: true,
};

export const SourceCardActionsMessages = defineMessages({
  addToCollection: { id: 't.addToCollection', defaultMessage: 'Collect' },
  addToPlaylist: { id: 't.addToPlaylist', defaultMessage: 'Add to Playlist' },
  createStation: { id: 't.createStation', defaultMessage: 'Create Station' },
  delete: { id: 't.delete', defaultMessage: 'Delete' },
  deletePlaylist: { id: 't.deletePlaylist', defaultMessage: 'Delete Playlist' },
  deleteStation: { id: 't.deleteStation', defaultMessage: 'Delete Station' },
  editStation: { id: 't.editStation', defaultMessage: 'Edit Station' },
  ellipsis: { id: 't.ellipsis', defaultMessage: 'Actions available' },
  newPlaylist: { id: 't.newPlaylist', defaultMessage: 'New Playlist' },
  openInDesktopApp: { id: 't.openInDesktopApp', defaultMessage: 'Open in Desktop App' },
  removeFromCollection: {
    id: 't.removeFromCollection',
    defaultMessage: 'Remove from your collection',
  },
  removeFromPlaylist: { id: 't.removeFromPlaylist', defaultMessage: 'Remove from Playlist' },
  share: { id: 't.share', defaultMessage: 'Share' },
  startStationArtist: { id: 't.createStationArtist', defaultMessage: 'Start Station from Artist' },
  startStationTrack: { id: 't.createStationTrack', defaultMessage: 'Start Station from Song' },
  tired: { id: 't.tired', defaultMessage: "I'm Tired of This Song" },
  whySelected: { id: 't.whySelected', defaultMessage: 'Why This Song?' },
} as const);

// the types of components that trigger sourcecards
export const SourceCardTriggerTypes = Object.freeze({
  ELLIPSIS_TRIGGER: 'ellipsisTrigger',
  STATION_TRIGGER: 'stationTrigger',
} as const);
